import { ReactNode, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Flex,
  SubmitIcon,
  TabNav,
  TabType,
  useStickySafeNavigate,
  useStyledTheme,
  Tab,
  SubmitButton,
  SubmitButtonTitle,
  useCreateAndAddRefs,
  useRefs,
  useHeaderHeight,
  flattenErrors,
  ErrorTypes,
  NextIcon,
  useIsMsalAuth,
  useIsUserAuth,
  Tooltip,
} from '@equifinance/shared';
import { useCreditContext } from '../../contexts/credit';
import { ErrorDisplayType, useAccountContext, useProductsContext } from '../../contexts';
import { Stages } from '../../types/Stages';
import { useNotesContext } from '../../contexts/notes';
import { useChecklistContext } from '../../contexts/checklist';
import { TransitionStageContext, TransitionStatusType } from '../../api/useAccountApi';
import useTransitionConfirm from './hooks/useTransitionConfirm';

export enum ApplicationPageType {
  CASE_DETAILS,
  CREDIT,
  PRODUCT,
  TASKS,
  CHECKLIST,
  AUDIT,
  NOTES,
}

type AccountTabNavProps = {
  page: ApplicationPageType,
}

export const SubmitButtonContainer = styled.div`
  width: 20%;
`;

const Wrapper = styled(Flex) <{ top: number }>`
  top:${({ top }) => top}px;
  gap: ${({ theme }) => theme.padding[2]};
  justify-content: space-between;
  position:sticky;
  z-index:3;
  background: ${({ theme }) => theme.colours.neutral[8]};
  padding-bottom: ${({ theme }) => theme.padding[4]};
  padding-top: ${({ theme }) => theme.padding[1]};
`;

type TabProps = {
  tabs: Tab[],
  underwriterTabs: Tab[],
  buttonSubmitTitle: ReactNode,
  stageChange: Stages,
  nextIcon: boolean,
}

export enum TabNavRefNames {
  TAB_NAV = 'tab-nav',
}

export default function AccountTabNav({ page }: AccountTabNavProps) {
  const [isTransitioning, setIsTransitioning] = useState<boolean>(false);
  const {
    id: accountId,
    creditSearchCompleted,
    account,
    transitionStatus: { transitionLabel, transitionStatus, reason },
    makeTransition,
  } = useAccountContext();

  const {
    productLoanDetails,
  } = useProductsContext();

  const {
    credit,
    fetchCredit,
  } = useCreditContext();

  const {
    notes,
  } = useNotesContext();

  const {
    checklist,
  } = useChecklistContext();

  const { stage } = account;

  const navigate = useStickySafeNavigate();
  const theme = useStyledTheme();
  const currentPageTabType = (tabPage: ApplicationPageType) => (page === tabPage ? TabType.PRIMARY : TabType.SECONDARY);

  const accountErrorsCount = flattenErrors(account?.errors)?.filter(
    (e) => e.errorType === ErrorTypes.ValidationFailure,
  )?.length ?? 0;
  const accountHasRejectPolicyErrors = (flattenErrors(account?.errors).filter(
    (e) => e.id === account.id && e.errorType === ErrorTypes.RejectPolicy,
  )?.length ?? 0) > 0;
  const accountHasRejectDataErrors = (flattenErrors(account?.errors).filter(
    (e) => e.id === account.id && e.errorType === ErrorTypes.RejectData,
  )?.length ?? 0) > 0;
  const accountHasReferWarnings = (flattenErrors(account?.warnings).filter(
    (e) => e.id === account.id && e.errorType === ErrorTypes.Refer,
  )?.length ?? 0) > 0;

  const creditErrorsCount = flattenErrors(credit?.errors)?.filter(
    (e) => e.errorType === ErrorTypes.ValidationFailure,
  )?.length ?? 0;
  const creditHasRejectPolicyErrors = (flattenErrors(credit?.errors)?.filter(
    (e) => e.id === credit.id && e.errorType === ErrorTypes.RejectPolicy,
  )?.length ?? 0) > 0;
  const creditHasRejectDataErrors = (flattenErrors(credit?.errors)?.filter(
    (e) => e.id === credit.id && e.errorType === ErrorTypes.RejectData,
  )?.length ?? 0) > 0;
  const creditHasReferWarnings = (flattenErrors(credit?.warnings)?.filter(
    (e) => e.id === credit.id && e.errorType === ErrorTypes.Refer,
  )?.length ?? 0) > 0;

  const productErrorsCount = flattenErrors(productLoanDetails.errors)?.filter(
    (e) => e.errorType === ErrorTypes.ValidationFailure,
  )?.length ?? 0;
  const productHasRejectPolicyErrors = (flattenErrors(productLoanDetails.errors)?.filter(
    (e) => e.id === account.id && e.errorType === ErrorTypes.RejectPolicy,
  )?.length ?? 0) > 0;
  const productHasRejectDataErrors = (flattenErrors(productLoanDetails.errors)?.filter(
    (e) => e.id === account.id && e.errorType === ErrorTypes.RejectData,
  )?.length ?? 0) > 0;
  const productHasReferWarnings = (flattenErrors(productLoanDetails.warnings)?.filter(
    (e) => e.id === account.id && e.errorType === ErrorTypes.Refer,
  )?.length ?? 0) > 0;

  const checklistPageErrors = [...flattenErrors(checklist.errors), ...flattenErrors(notes.errors)];
  const checklistPageWarnings = [...flattenErrors(checklist.warnings), ...flattenErrors(notes.warnings)];

  const checklistErrorsCount = checklistPageErrors?.filter(
    (e) => e.errorType === ErrorTypes.ValidationFailure,
  )?.length ?? 0;
  const checklistHasRejectPolicyErrors = (checklistPageErrors.filter(
    (e) => e.id === checklist.id && e.errorType === ErrorTypes.RejectPolicy,
  )?.length ?? 0) > 0;
  const checklistHasRejectDataErrors = (checklistPageErrors.filter(
    (e) => e.id === checklist.id && e.errorType === ErrorTypes.RejectData,
  )?.length ?? 0) > 0;
  const checklistHasReferWarnings = (checklistPageWarnings.filter(
    (e) => e.id === checklist.id && e.errorType === ErrorTypes.Refer,
  )?.length ?? 0) > 0;

  const errorCountToPercent = (errorCount: number) => (errorCount > 0 ? 50 : 100);

  const caseDetailsTab: Tab = {
    id: 'case-details-tab',
    tabType: currentPageTabType(ApplicationPageType.CASE_DETAILS),
    label: 'Application Details',
    percentComplete: errorCountToPercent(accountErrorsCount),
    hasRejectPolicyErrors: accountHasRejectPolicyErrors,
    hasRejectDataErrors: accountHasRejectDataErrors,
    hasReferWarnings: accountHasReferWarnings,
    onClick: () => navigate(`/application/${accountId}`),
  };

  const creditTab: Tab = {
    id: 'credit-tab',
    tabType: currentPageTabType(ApplicationPageType.CREDIT),
    label: 'Credit',
    percentComplete: errorCountToPercent(creditErrorsCount),
    hasRejectPolicyErrors: creditHasRejectPolicyErrors,
    hasRejectDataErrors: creditHasRejectDataErrors,
    hasReferWarnings: creditHasReferWarnings,
    onClick: () => {
      fetchCredit();
      navigate(`/credit/${accountId}`);
    },
  };

  const productTab: Tab = {
    id: 'product-tab',
    tabType: currentPageTabType(ApplicationPageType.PRODUCT),
    label: 'Product',
    percentComplete: creditSearchCompleted ? errorCountToPercent(productErrorsCount) : 0,
    hasRejectPolicyErrors: productHasRejectPolicyErrors,
    hasRejectDataErrors: productHasRejectDataErrors,
    hasReferWarnings: productHasReferWarnings,
    disabled: !creditSearchCompleted,
    onClick: () => navigate(`/products/${accountId}`),
  };

  const tasksTab: Tab = {
    id: 'tasks-tab',
    tabType: currentPageTabType(ApplicationPageType.TASKS),
    label: 'Tasks',
    percentComplete: 0,
    hasRejectPolicyErrors: false,
    hasRejectDataErrors: false,
    hasReferWarnings: false,
    onClick: () => navigate(`/tasks/${accountId}`),
  };

  const checklistTab: Tab = {
    id: 'checklist-tab',
    tabType: currentPageTabType(ApplicationPageType.CHECKLIST),
    label: 'Checklist (Int.)',
    percentComplete: errorCountToPercent(checklistErrorsCount),
    hasRejectPolicyErrors: checklistHasRejectPolicyErrors,
    hasRejectDataErrors: checklistHasRejectDataErrors,
    hasReferWarnings: checklistHasReferWarnings,
    onClick: () => navigate(`/checklist/${accountId}`),
  };

  const auditTab: Tab = {
    id: 'audit-tab',
    tabType: currentPageTabType(ApplicationPageType.AUDIT),
    label: 'Audit History',
    hasRejectPolicyErrors: false,
    hasRejectDataErrors: false,
    hasReferWarnings: false,
    onClick: () => navigate(`/audit/${accountId}`),
  };

  const notesTab: Tab = {
    id: 'notes-tab',
    tabType: currentPageTabType(ApplicationPageType.NOTES),
    label: 'Notes (Int.)',
    percentComplete: 0,
    hasRejectPolicyErrors: false,
    hasRejectDataErrors: false,
    hasReferWarnings: false,
    onClick: () => navigate(`/notes/${accountId}`),
  };

  const isInternalUser = useIsMsalAuth();
  const isExternalUser = useIsUserAuth();

  const tabs: Tab[] = [
    caseDetailsTab,
    ...(((isInternalUser && creditSearchCompleted) || (isExternalUser && stage !== Stages.CreditDecision)) ? [creditTab] : []),
    productTab,
    ...(((isInternalUser && creditSearchCompleted) || (isExternalUser && ![Stages.CreditDecision, Stages.Affordability].includes(stage))) ? [tasksTab] : []),
  ];

  const underwriterTabs: Tab[] = [
    ...((isInternalUser && creditSearchCompleted) ? [checklistTab] : []),
    ...((isInternalUser && creditSearchCompleted) ? [auditTab] : []),
  ];

  const buttonSubmitTitle = <>{transitionLabel}</>;

  const defaultTabProperties: TabProps = {
    tabs: [caseDetailsTab, creditTab, productTab, tasksTab],
    underwriterTabs: [checklistTab, auditTab, notesTab],
    buttonSubmitTitle,
    stageChange: Stages.AtLender,
    nextIcon: false,
  };

  const tabProperties: Record<string, TabProps> = {
    [`${Stages.CreditDecision}`]: {
      tabs,
      underwriterTabs,
      buttonSubmitTitle,
      stageChange: Stages.Affordability,
      nextIcon: true,
    },
    [`${Stages.Affordability}`]: {
      tabs,
      underwriterTabs,
      buttonSubmitTitle,
      stageChange: Stages.Packaging,
      nextIcon: true,
    },
    [`${Stages.Packaging}`]: {
      tabs,
      underwriterTabs,
      buttonSubmitTitle,
      stageChange: Stages.AtLender,
      nextIcon: isInternalUser,
    },
    [`${Stages.AtLender}`]: {
      tabs,
      underwriterTabs,
      buttonSubmitTitle,
      stageChange: Stages.Offer,
      nextIcon: true,
    },
    [`${Stages.Offer}`]: {
      tabs,
      underwriterTabs,
      buttonSubmitTitle,
      stageChange: Stages.Completion,
      nextIcon: true,
    },
    [`${Stages.Completion}`]: {
      tabs,
      underwriterTabs,
      buttonSubmitTitle,
      stageChange: Stages.Completion,
      nextIcon: false,
    },
  };

  const transitionAllowed = transitionStatus === TransitionStatusType.TransitionAllowed && !isTransitioning;
  const transitionErrorTooltip = useMemo(() => {
    const errors = reason?.errors || [];
    const warnings = reason?.warnings || [];
    const messages = [...errors, ...warnings]
      .filter((e) => e.displayType === ErrorDisplayType.Tooltip)
      .flatMap((e) => e.messages);
    if (messages.length) {
      const label = errors.length > 0
        ? `${transitionLabel} can't be undertaken, please see details below:`
        : `${transitionLabel} can be undertaken. However there are warnings to be aware of, please see details below:`;
      return messages?.reduce(
        (prev, current) => `${prev}<br/>&emsp;&bull;&nbsp;${current}`,
        label,
      );
    }
    return null;
  }, [reason]);

  const tabProps = tabProperties?.[`${stage}`] ?? defaultTabProperties;

  const tabNavsRef = useCreateAndAddRefs(TabNavRefNames.TAB_NAV);

  const headerHeight = useHeaderHeight();

  const { showCompleteApplicationModal, showSubmitApplcationModal } = useTransitionConfirm();

  const handleTransition = (stageContext?: TransitionStageContext) => {
    setIsTransitioning(true);
    makeTransition(stageContext)
      .finally(() => {
        setIsTransitioning(false);
      });
  };

  const onClickSubmit = () => {
    if (stage === Stages.Packaging) {
      showSubmitApplcationModal(
        account?.consentToSubmit?.htmlText,
        () => handleTransition({ hasConsent: true, readyForServicing: false }),
      );
      return;
    }
    if (stage === Stages.Completion) {
      showCompleteApplicationModal(
        () => handleTransition({ hasConsent: false, readyForServicing: true }),
      );
      return;
    }
    handleTransition();
  };
  return (
    <Wrapper id={TabNavRefNames.TAB_NAV} ref={tabNavsRef} top={headerHeight}>
      <TabNav
        tabs={tabProps.tabs}
        underwriterTabs={tabProps.underwriterTabs}
        submitButton={transitionStatus !== TransitionStatusType.TransitionNotEligible ? (
          <SubmitButtonContainer>
            <SubmitButton
              id="transition-button"
              onClick={onClickSubmit}
              disabled={!transitionAllowed}
              data-testid="submit-button"
            >
              <SubmitButtonTitle>{tabProps.buttonSubmitTitle}</SubmitButtonTitle>
              {tabProps.nextIcon
                ? (<NextIcon fill={transitionAllowed ? theme.colours.primary[1] : theme.colours.neutral[5]} />)
                : (<SubmitIcon fill={transitionAllowed ? theme.colours.primary[1] : theme.colours.neutral[5]} />)}

            </SubmitButton>
            {transitionErrorTooltip && (
              <Tooltip
                id="transition-button"
                html={transitionErrorTooltip}
                widthOverload={400}
                place="bottom"
              />
            )}
          </SubmitButtonContainer>
        ) : <></>}
      />

    </Wrapper>
  );
}

export const useTabNavHeight = () => {
  const { refs } = useRefs();
  const headerHeight = useHeaderHeight();
  return (refs[TabNavRefNames.TAB_NAV]?.current?.clientHeight ?? 0) + headerHeight;
};
